import React, { useCallback, useEffect, useId, useMemo, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { SearchFilter } from './search-filter'
import { NodeSearchResults } from './node-search-results'
import { useNodeSearch } from './use-node-search'
import { useSearchParam } from './use-search-param'
import { hasFormat } from './search-areas'
import { PlatformSelect } from '../platform/platform-controls'
import { takePlatformShortcut } from '../platform/platforms'
import { FormatSelect } from '../format/format-controls'
import { keepFormat, takeFormatShortcut } from '../format/formats'
import '../platform/platform-controls.scss'
import './node-search.scss'

// "/" focuses the page's search box from anywhere on it (owner 2026-10-09:
// Cmd/Ctrl+K is kept for a future site-wide search, so there is no handler
// for it here). Not while the person is typing in a field, nor with a
// modifier held, nor behind an open dialog.
const isTyping = target => {
  if (!target || !target.tagName) return false
  const tag = target.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if (target.isContentEditable) return true
  return !!(target.closest && target.closest('[contenteditable]:not([contenteditable="false"])'))
}

// A dialog that is shown, not one merely mounted: pages keep Bootstrap
// modals in the DOM, hidden, with role="dialog" and aria-modal (dev,
// 2026-10-09: one such made "/" do nothing).
const shown = el => !(el.classList.contains('modal') && !el.classList.contains('show')) && el.getAttribute('aria-hidden') !== 'true' && !el.hidden
const dialogOpen = () =>
  typeof document !== 'undefined' &&
  (document.body.classList.contains('modal-open') || [...document.querySelectorAll('[role="dialog"][aria-modal="true"], dialog[open]')].some(shown))

// A node area's search (spec 2026-10-09 §5): the search row (box, "Available
// on" where the area has builds, and "Format" where the page passes
// onFormatChange: Content and Templates, for super admins only) and, while
// the box has text,
// the ranked results in place of the grid (`children`). The viewer's search,
// run in the browser over the list this page already loaded:
//
//   area       CONTENT_SEARCH, TEMPLATE_SEARCH or LIBRARY_SEARCH
//   nodes      the area's full list (paths for the results' links)
//   items      the list to search: `nodes`, or content's answer for the
//              platform chosen; the format narrows it here
//   thumbnails the area's asset-thumbnail rows (the cards' pictures)
//   loaded / error / onRetry   that list's state
//
// The query is the URL's ?search=. Keyboard (WAI-ARIA combobox): ↑/↓ move
// the active row (the first by default), Enter opens it, Cmd/Ctrl+Enter in a
// new tab, Esc clears the box (or leaves it when empty), "/" comes back.
export const NodeSearchArea = ({ area, nodes, items, thumbnails, loaded, error, onRetry, platform, onPlatformChange, format, onFormatChange, children }) => {
  const history = useHistory()
  const [value, setValue] = useSearchParam()
  const inputRef = useRef(null)
  const listId = `node-search-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

  const corpusItems = useMemo(() => (format ? keepFormat(items, format, hasFormat(area)) : items), [items, format, area])
  const search = useNodeSearch({ items: corpusItems, kinds: area.kinds, query: value })

  const filterable = !!onPlatformChange
  const formattable = !!onFormatChange

  const handleChange = text => {
    let rest = text
    if (filterable) {
      const shortcut = takePlatformShortcut(rest)
      if (shortcut.platform) {
        onPlatformChange(shortcut.platform)
        rest = shortcut.text
      }
    }
    if (formattable) {
      const shortcut = takeFormatShortcut(rest)
      if (shortcut.format) {
        onFormatChange(shortcut.format)
        rest = shortcut.text
      }
    }
    // Leading spaces mean nothing to the search; dropping them keeps "v2: "
    // typed a key at a time from leaving the box (and the URL) starting with
    // a space.
    setValue(rest.replace(/^\s+/, ''))
  }

  const hrefOf = result => area.href(nodes || [], result.entry.node)

  // The area's thumbnails (content's asset-thumbnail rows, the cards' own
  // list), by node: one lookup per row.
  const thumbnailByNode = useMemo(() => new Map((thumbnails || []).map(asset => [asset.nodeID, asset])), [thumbnails])
  const thumbnailOf = useCallback(id => thumbnailByNode.get(id), [thumbnailByNode])

  const open = (result, newTab) => {
    if (!result) return
    const href = hrefOf(result)
    if (newTab) window.open(href, '_blank', 'noopener')
    else history.push(href)
  }

  const handleKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
        if (!search.results.length) return
        e.preventDefault()
        search.move(1)
        break
      case 'ArrowUp':
        if (!search.results.length) return
        e.preventDefault()
        search.move(-1)
        break
      case 'Enter': {
        // Enter before the last keystroke was scored opens what is typed
        // now, never a row of the query before it.
        const results = search.pending ? search.resultsNow() : search.results
        const result = search.pending ? results[0] : results[search.active]
        if (!result) return
        e.preventDefault()
        open(result, e.metaKey || e.ctrlKey)
        break
      }
      case 'Escape':
        e.preventDefault()
        if (value) setValue('')
        else if (inputRef.current) inputRef.current.blur()
        break
      default:
        break
    }
  }

  useEffect(() => {
    const onKey = e => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return
      if (isTyping(e.target) || dialogOpen()) return
      if (!inputRef.current) return
      e.preventDefault()
      inputRef.current.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const showing = !!value.trim()
  const activeID = showing && search.active >= 0 ? `${listId}-${search.active}` : undefined

  return (
    <div className="d-flex flex-column h-100">
      <div className="node-search-bar m-2">
        <div className="node-search">
          <SearchFilter
            value={value}
            onChange={handleChange}
            placeholder={area.placeholder}
            inputRef={inputRef}
            onKeyDown={handleKeyDown}
            combobox={{
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': showing,
              'aria-controls': listId,
              'aria-activedescendant': activeID
            }}
          />
        </div>
        {filterable && <PlatformSelect value={platform} onChange={onPlatformChange} />}
        {formattable && <FormatSelect value={format} onChange={onFormatChange} />}
      </div>
      <div className="flex-fill" style={{ minHeight: 0 }}>
        <div className="overflow-scroll h-100">
          {showing
            ? <NodeSearchResults
                area={area}
                nodes={nodes || []}
                thumbnailOf={thumbnailOf}
                search={search}
                listId={listId}
                hrefOf={hrefOf}
                query={value.trim()}
                loaded={loaded}
                error={error}
                onRetry={onRetry}
                platform={platform}
                format={format}
                onClear={() => {
                  setValue('')
                  if (inputRef.current) inputRef.current.focus()
                }}
                onClearPlatform={filterable && platform ? () => onPlatformChange(null) : null}
                onClearFormat={formattable && format ? () => onFormatChange(null) : null}
              />
            : children}
        </div>
      </div>
    </div>
  )
}
